
import { useState, useCallback, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { DRIVERS_2026, TEAMS_2026 } from '../data/f1data2026';

export default function F1Standings({ onBack }) {
  const [tab, setTab]                   = useState('drivers');
  const [driverStandings, setDriverStandings]       = useState([]);
  const [constructorStandings, setConstructorStandings] = useState([]);
  const [loading, setLoading]           = useState(true);

  const onMsg = useCallback((msg) => {
    if (msg.type === 'F1_SNAPSHOT') {
      if (msg.snapshot?.driverStandings?.length > 0)       setDriverStandings(msg.snapshot.driverStandings);
      if (msg.snapshot?.constructorStandings?.length > 0)  setConstructorStandings(msg.snapshot.constructorStandings);
      setLoading(false);
    }
  }, []);

  const { send } = useWebSocket(onMsg);
  useEffect(() => { send('F1_GET_SNAPSHOT'); }, [send]);

  const hasLiveDrivers = driverStandings.length > 0;
  const hasLiveCons    = constructorStandings.length > 0;
  const maxPts  = hasLiveDrivers ? Math.max(...driverStandings.map(d => d.pts ?? d.points ?? 0), 1) : 1;
  const maxCPts = hasLiveCons   ? Math.max(...constructorStandings.map(c => c.pts ?? c.points ?? 0), 1) : 1;

  return (
    <div className="layer" style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="g-hdr f1-hdr">
        <button className="g-back" onClick={onBack}>← Back</button>
        <div className="g-title" style={{color:'#e8002d'}}>F1 2026</div>
        <div style={{fontSize:'0.58rem',color:hasLiveDrivers?'#22c55e':'#4a6a9e'}}>
          {loading ? 'Loading...' : hasLiveDrivers ? '● Live' : 'Confirmed Grid'}
        </div>
      </div>

      <div className="game-body" style={{maxWidth:640}}>
        <div className="f1-standings-tabs">
          <button className={'f1-standings-tab'+(tab==='drivers'?' active':'')}       onClick={()=>setTab('drivers')}>🏎️ Drivers</button>
          <button className={'f1-standings-tab'+(tab==='constructors'?' active':'')}  onClick={()=>setTab('constructors')}>🏭 Teams</button>
          <button className={'f1-standings-tab'+(tab==='grid'?' active':'')}          onClick={()=>setTab('grid')}>📋 Full Grid</button>
        </div>

        {/* DRIVER STANDINGS — live from Jolpica, fallback to grid */}
        {tab === 'drivers' && (
          hasLiveDrivers ? (
            <div className="f1-leaderboard">
              <div className="f1-lb-hdr">
                <span style={{width:26}}>P</span>
                <span style={{width:4}}/>
                <span style={{flex:1}}>Driver</span>
                <span style={{minWidth:56,textAlign:'right'}}>Pts</span>
                <span style={{minWidth:28,textAlign:'right'}}>W</span>
              </div>
              {driverStandings.map((d,i) => {
                const pts   = d.pts ?? d.points ?? 0;
                const wins  = d.wins ?? 0;
                // Match to 2026 grid for team colour
                const g2026 = DRIVERS_2026.find(x =>
                  x.acronym === (d.acronym || d.code) ||
                  (d.driver||'').includes(x.name.split(' ').pop())
                );
                const colour = g2026?.colour || '#3a5a8e';
                return (
                  <div key={i} className="f1-lb-row" style={{flexDirection:'column',gap:5}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,width:'100%'}}>
                      <span className={'f1-pos '+(i===0?'p1':i===1?'p2':i===2?'p3':'other')}>{d.position||i+1}</span>
                      <div className="f1-team-bar" style={{background:colour}}/>
                      <div className="f1-driver" style={{flex:1}}>
                        <span className="f1-driver-name">{d.driver || d.fullName}</span>
                        <span className="f1-driver-team">{d.team || d.teamName}</span>
                      </div>
                      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.3rem',color:'#e8edf8',minWidth:48,textAlign:'right'}}>{pts}</span>
                      {wins > 0 && <span style={{fontSize:'0.7rem',color:'#ffd700',minWidth:28,textAlign:'right'}}>{wins}W</span>}
                    </div>
                    <div style={{paddingLeft:34}}>
                      <div style={{height:3,borderRadius:2,background:'#1a2a4a',overflow:'hidden'}}>
                        <div style={{height:'100%',width:(pts/maxPts*100)+'%',background:colour,borderRadius:2,transition:'width 0.5s'}}/>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{textAlign:'center',padding:24}}>
              {loading
                ? <p style={{color:'#8ba3cc',fontSize:'0.8rem'}}>Fetching live standings...</p>
                : <>
                    <p style={{color:'#8ba3cc',fontSize:'0.84rem'}}>Season standings not yet available</p>
                    <p style={{color:'#4a6a9e',fontSize:'0.7rem',marginTop:6}}>Standings update after each race weekend</p>
                    <button className="f1-standings-tab active" style={{marginTop:12}} onClick={()=>setTab('grid')}>
                      View 2026 Grid →
                    </button>
                  </>
              }
            </div>
          )
        )}

        {/* CONSTRUCTOR STANDINGS */}
        {tab === 'constructors' && (
          hasLiveCons ? (
            <div className="f1-leaderboard">
              <div className="f1-lb-hdr">
                <span style={{width:26}}>P</span>
                <span style={{width:4}}/>
                <span style={{flex:1}}>Constructor</span>
                <span style={{minWidth:56,textAlign:'right'}}>Pts</span>
              </div>
              {constructorStandings.map((c,i) => {
                const pts    = c.pts ?? c.points ?? 0;
                const t2026  = TEAMS_2026.find(t => (c.team||'').includes(t.team.split(' ')[0]));
                const colour = t2026?.colour || c.colour || '#3a5a8e';
                return (
                  <div key={i} className="f1-lb-row" style={{flexDirection:'column',gap:5}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,width:'100%'}}>
                      <span className={'f1-pos '+(i===0?'p1':i===1?'p2':i===2?'p3':'other')}>{c.position||i+1}</span>
                      <div className="f1-team-bar" style={{background:colour,height:36}}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:'0.88rem',fontWeight:700,color:'#e8edf8'}}>{c.team}</div>
                        {t2026 && <div style={{fontSize:'0.58rem',color:'#4a6a9e'}}>{t2026.drivers.join(' · ')}</div>}
                      </div>
                      <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.4rem',color:'#e8edf8'}}>{pts}</span>
                    </div>
                    <div style={{paddingLeft:34}}>
                      <div style={{height:4,borderRadius:2,background:'#1a2a4a',overflow:'hidden'}}>
                        <div style={{height:'100%',width:(pts/maxCPts*100)+'%',background:colour,borderRadius:2,transition:'width 0.5s'}}/>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{textAlign:'center',padding:24}}>
              {loading
                ? <p style={{color:'#8ba3cc',fontSize:'0.8rem'}}>Fetching standings...</p>
                : <>
                    <p style={{color:'#8ba3cc',fontSize:'0.84rem'}}>Constructor standings not yet available</p>
                    <p style={{color:'#4a6a9e',fontSize:'0.7rem',marginTop:6}}>Updates after each race</p>
                    <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:16}}>
                      {TEAMS_2026.map(t => (
                        <div key={t.team} style={{background:'#0f1e38',border:'1px solid #1e3a6e',borderRadius:9,padding:'10px 14px',
                          display:'flex',alignItems:'center',gap:10}}>
                          <div style={{width:5,height:36,borderRadius:3,background:t.colour,flexShrink:0}}/>
                          <div style={{flex:1}}>
                            <div style={{fontSize:'0.84rem',fontWeight:700,color:'#e8edf8'}}>{t.nat} {t.team}</div>
                            <div style={{fontSize:'0.62rem',color:'#4a6a9e',marginTop:2}}>{t.drivers.join(' · ')}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
              }
            </div>
          )
        )}

        {/* FULL 2026 GRID */}
        {tab === 'grid' && (
          <div className="f1-leaderboard">
            <div className="f1-lb-hdr">
              <span style={{width:32}}>No.</span>
              <span style={{width:4}}/>
              <span style={{flex:1}}>Driver</span>
              <span style={{minWidth:32,textAlign:'right'}}>Nat</span>
            </div>
            {DRIVERS_2026.map(d => (
              <div key={d.num} className="f1-lb-row">
                <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:'1.2rem',color:'#3a5a8e',width:32,textAlign:'center'}}>{d.num}</span>
                <div className="f1-team-bar" style={{background:d.colour}}/>
                <div className="f1-driver" style={{flex:1}}>
                  <span className="f1-driver-name">{d.name}</span>
                  <span className="f1-driver-team">{d.team}</span>
                </div>
                <span style={{fontSize:'1rem',minWidth:32,textAlign:'right'}}>{d.nat}</span>
              </div>
            ))}
          </div>
        )}

        <p style={{fontSize:'0.6rem',color:'#3a5a6e',textAlign:'center',marginTop:4}}>
          {hasLiveDrivers ? 'Live 2026 standings via Jolpica F1 API' : '2026 confirmed driver lineup'}
          {' · Live session data via OpenF1'}
        </p>
      </div>
    </div>
  );
}
